<template>
  <div v-if="scannerState.visible" class="scanner-layer">
    <video
      ref="videoRef"
      autoplay
      playsinline
      webkit-playsinline
      muted
    />
    <div class="scanner-layer__mask">
      <button type="button" class="scanner-layer__close" @click="closePopup">
        <van-icon name="cross" />
      </button>
      <div class="scanner-layer__frame">
        <app-image src="@/assets/images/homeImage/scan_line.png" alt="" />
      </div>
      <p class="scanner-layer__hint">{{ t('wallet.scanCode') }}</p>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { showFailToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import jsQR from 'jsqr';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useWalletScanner } from '@/composables/useWalletScanner';
import { navigateWithState } from '@/utils/route-state';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const {
  scannerState,
  resetWalletScanner,
  getWalletScannerStream,
  stopWalletScannerStream,
  showWalletScannerGuide
} = useWalletScanner();

const videoRef = ref(null);

let stream = null;
let detector = null;
let timer = null;
let playRetryTimer = null;

function clearDetectTimer() {
  if (timer) {
    window.clearInterval(timer);
    timer = null;
  }
}

async function bindStream() {
  stream = getWalletScannerStream();
  if (!stream || !videoRef.value) {
    if (videoRef.value) {
      videoRef.value.srcObject = null;
    }
    return;
  }

  videoRef.value.muted = true;
  videoRef.value.setAttribute('autoplay', 'true');
  videoRef.value.setAttribute('playsinline', 'true');
  videoRef.value.setAttribute('webkit-playsinline', 'true');
  videoRef.value.srcObject = stream;
  const playable = await startVideoPlayback();
  if (!playable) {
    closePopup();
    return;
  }
  detector = 'BarcodeDetector' in window
    ? new window.BarcodeDetector({ formats: ['qr_code'] })
    : null;
  clearDetectTimer();
  timer = window.setInterval(detectFrame, 500);
}

async function startVideoPlayback() {
  const video = videoRef.value;
  if (!video) {
    return false;
  }

  try {
    await video.play?.();
    return true;
  } catch (error) {
    await new Promise((resolve) => {
      playRetryTimer = window.setTimeout(resolve, 120);
    });
  }

  try {
    await video.play?.();
    return true;
  } catch (error) {
    showFailToast(t('wallet.cameraUnavailable'));
    showWalletScannerGuide('unavailable');
    return false;
  }
}

function stopCamera() {
  clearDetectTimer();
  if (playRetryTimer) {
    window.clearTimeout(playRetryTimer);
    playRetryTimer = null;
  }
  stopWalletScannerStream();
  stream = null;
  detector = null;
  if (videoRef.value) {
    videoRef.value.srcObject = null;
  }
}

async function detectFrame() {
  const video = videoRef.value;
  if (!video || !detector || video.readyState < 2) {
    return;
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const context = canvas.getContext('2d');
  context?.drawImage(video, 0, 0, canvas.width, canvas.height);

  try {
    if (detector) {
      const codes = await detector.detect(canvas);
      if (codes.length) {
        finishScan(codes[0].rawValue);
      }
      return;
    }

    const imageData = context?.getImageData(0, 0, canvas.width, canvas.height);
    if (!imageData) {
      return;
    }

    const code = jsQR(imageData.data, canvas.width, canvas.height, {
      inversionAttempts: 'dontInvert'
    });
    if (code?.data) {
      finishScan(code.data);
    }
  } catch (error) {
    clearDetectTimer();
  }
}

function closePopup() {
  stopCamera();
  resetWalletScanner();
}

function finishScan(value) {
  const result = String(value || '').trim();
  if (!result) {
    return;
  }

  stopCamera();
  showPassedToast(t('wallet.scanSuccess'));

  const targetPath = scannerState.back || route.path;
  const nextPayload = Object.keys(scannerState.statePayload || {}).length
    ? { ...scannerState.statePayload }
    : {};
  nextPayload.scanResult = result;
  resetWalletScanner();
  if (scannerState.stateNamespace) {
    navigateWithState(router, targetPath, {
      namespace: scannerState.stateNamespace,
      payload: nextPayload,
      replace: true
    });
    return;
  }
  const nextQuery = Object.keys(scannerState.query || {}).length
    ? { ...scannerState.query }
    : { ...route.query };
  nextQuery.scanResult = result;
  router.replace({
    path: targetPath,
    query: nextQuery
  });
}

watch(
  () => [scannerState.visible, scannerState.streamVersion],
  async ([visible]) => {
    if (visible) {
      await nextTick();
      bindStream();
      return;
    }
    stopCamera();
  }
);

onBeforeUnmount(stopCamera);
</script>

<style scoped lang="scss">
.scanner-layer {
  position: fixed;
  inset: 0;
  z-index: 3000;
  overflow: hidden;
  background: #111827;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.scanner-layer__mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    rgba(17, 24, 39, 0.72),
    rgba(17, 24, 39, 0.22) 35%,
    rgba(17, 24, 39, 0.22) 65%,
    rgba(17, 24, 39, 0.72)
  );
}

.scanner-layer__close {
  position: absolute;
  top: 50px;
  right: 18px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 18px;
  background: rgba(17, 24, 39, 0.36);
  color: #ffffff;
  font-size: 20px;
}

.scanner-layer__frame {
  position: relative;
  width: 250px;
  height: 250px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 8px;

  &::before,
  &::after {
    position: absolute;
    width: 38px;
    height: 38px;
    border-color: #ffffff;
    border-style: solid;
    content: '';
  }

  &::before {
    top: -2px;
    left: -2px;
    border-width: 4px 0 0 4px;
  }

  &::after {
    right: -2px;
    bottom: -2px;
    border-width: 0 4px 4px 0;
  }

  .app-image {
    position: absolute;
    top: 18%;
    left: 0;
    width: 100%;
    animation: scanLine 2.1s linear infinite;
  }
}

.scanner-layer__hint {
  margin-top: 28px;
  color: #ffffff;
  font-size: 16px;
  line-height: 24px;
}

@keyframes scanLine {
  0% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(150px);
  }

  100% {
    transform: translateY(0);
  }
}
</style>
